import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  signal,
} from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { CustomizationRow } from '@app/model-composer/models/customization-group.model';
import { CustomizationOptionsComponent } from '@app/model-composer/components/customization-options.component';
import { CompositionSelectionService } from '@app/model-composer/services/composition-selection.service';
import { MaterialColorItemComponent } from '@app/model-composer/components/material-color-item.component';
import {IpeDebug} from '@app/model-composer/ipe-debug';
import {ComposerStateService} from '@app/model-composer';

@Component({
  selector: 'app-customization-row',
  standalone: true,
  imports: [CommonModule, NgClass, CustomizationOptionsComponent, MaterialColorItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ipe:bg-white" [class.ipe:p-6]="mode !== 'element'">
      <div class="ipe:flex ipe:items-center ipe:justify-between">
        <div class="ipe:flex ipe:gap-4 ipe:items-center">
          @let sel = selection();

          @if (sel) {
            <app-material-color-item
              size="md"
              class="ipe:flex-shrink-0"
              [data]="sel.item"
              [active]="true"
            />
          }

          <div class="ipe:flex ipe:flex-col ipe:min-w-0">
            <h2 class="ipe:text-sm ipe:mb-1 ipe:text-gray-600">
              {{ row.label }}
            </h2>

            @if (sel) {
              @if (sel.item?.color?.vestaCode && sel.item?.color?.vestaCode.toLowerCase().startsWith('con')) {
                <p class="ipe:text-sm ipe:text-gray-400 ipe:truncate ipe:text-ellipsis ipe:max-w-88">
                  {{ sel.item?.material?.materialName | titlecase }} - {{ sel.item?.color?.colorName | titlecase }}
                </p>
              }
              @else {
                <p class="ipe:text-sm ipe:text-gray-400 ipe:truncate ipe:text-ellipsis ipe:max-w-88">
                  {{ sel.item?.color?.colorName | titlecase }}
                </p>
              }
            } @else {
              <p class="ipe:text-sm ipe:text-gray-400">
                Nessuna specifica selezionata
              </p>
            }
          </div>
        </div>

        <div class="ipe:flex ipe:items-center ipe:gap-2">
          @if (canReset()) {
            <button
              type="button"
              (click)="reset()"
              class="
                ipe:inline-flex ipe:items-center ipe:justify-center
                ipe:w-10 ipe:h-10
                ipe:cursor-pointer
                ipe:rounded-sm
                ipe:border ipe:border-gray-200
                ipe:bg-white
                ipe:text-gray-400
                ipe:hover:text-black
                ipe:hover:border-black
              "
              aria-label="Ripristina default"
              title="Ripristina default"
            >
              ✕
            </button>
          }

          <button
            type="button"
            (click)="toggle()"
            class="
              ipe:inline-flex ipe:items-center ipe:justify-between ipe:gap-2
              ipe:px-6 ipe:py-3
              ipe:rounded-sm
              ipe:border ipe:border-gray-200
              ipe:bg-white
              ipe:text-[11px]  ipe:tracking-widest
              ipe:text-gray-400
              ipe:transition-colors ipe:duration-300
              ipe:hover:text-black
              ipe:hover:border-black
              ipe:cursor-pointer
            "
          >
            <span class="ipe:flex-1 ipe:text-left">
              {{ expanded() ? 'Chiudi' : 'Seleziona' }}
            </span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="ipe:w-3 ipe:h-3 ipe:transition-transform ipe:duration-300 ipe:ease-out"
              [ngClass]="expanded() ? 'ipe:rotate-180' : 'ipe:rotate-0'"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>
      </div>

      @if (expanded()) {
        <div class="ipe:mt-4" [class.ipe:px-5]="mode !== 'element'">
          <app-customization-options
            [options]="row.materialOptions"
            [selected]="selection()"
            (selectMaterialColor)="onSelect($event)"
          />
        </div>
      }
    </div>
  `,
  styles: [
    `
      @import 'tailwindcss' prefix(ipe);
      @theme {
        --font-display: 'ABC Walter Neue', sans-serif;
        --font-roboto: 'ABC Walter Neue', sans-serif;
        --font-sans: 'ABC Walter Neue', sans-serif;
      }
    `
  ]
})
export class CustomizationRowComponent {
  @Input() mode?: 'element' | 'group';
  @Input() scopeUid?: string;
  @Input({ required: true }) row!: CustomizationRow;

  private selectionService = inject(CompositionSelectionService);
  private state = inject(ComposerStateService);

  readonly expanded = signal(false);

  readonly selection = computed(() => {
    const rowId = this.row?.id;
    if (rowId == null) { return null; }

    if (this.scopeUid) {
      return this.selectionService.getElementSelection(this.scopeUid, rowId);
    }

    return this.selectionService.getSelection({rowId});
  });

  readonly canReset = computed(() => {
    const rowId = this.row?.id;
    if (rowId == null) { return false; }

    if (this.scopeUid) {
      const overridden = this.selectionService.isElementOverridden(this.scopeUid, rowId);
      if (overridden) { return true; }

      const hasSel = this.selection() != null;
      const hasDefault = this.selectionService.hasElementDefault(this.scopeUid, rowId);
      return hasSel && !hasDefault;
    }

    const overridden = this.selectionService.isOverridden(rowId);
    if (overridden) { return true; }

    const hasSel = this.selection() != null;
    const hasDefault = this.selectionService.hasDefault(rowId);
    return hasSel && !hasDefault;
  });

  reset(): void {
    const rowId = this.row?.id;
    if (rowId == null) { return; }

    const currentSelection = this.selectionService.getSelection({
      rowId,
      scopeUid: this.scopeUid,
    });

    this.state.resetMaterialPlacementSwapFromComposition({
      placementsToRemove: currentSelection?.placementsToRemove,
      placementsToRestore: currentSelection?.placementsToRestore,
    });

    this.selectionService.syncReset({
      rowId,
      scopeUid: this.scopeUid,
    });
  }

  toggle(): void {
    this.expanded.update(v => !v);
  }

  onSelect(event: {
    optionId: string;
    materialColor: any;
    item: any;
    eligiblePlacements?: string[];
    placementsToRemove?: { code: string; placements: string[] }[];
    placementsToRestore?: { code: string; placements: string[] }[];
  }): void {
    const rowId = this.row?.id;
    if (rowId == null) { return; }

    const eligiblePlacements = Array.isArray(event.eligiblePlacements) && event.eligiblePlacements.length > 0 ? event.eligiblePlacements : undefined;


    this.state.applyMaterialPlacementSwapToComposition({
      placementsToRemove: event.placementsToRemove,
      placementsToRestore: event.placementsToRestore,
    });


    IpeDebug.event(
      'ui.color.onSelect',
      {
        mode: this.mode ?? null,
        scopeUid: this.scopeUid ?? null,
        rowId,
        rowLabel: this.row?.label ?? null,
        optionId: event.optionId,
        eligiblePlacementsFromEvent: event.eligiblePlacements ?? null,
        eligiblePlacementsUsed: eligiblePlacements ?? null,
        materialColor: event.materialColor,
        item: event.item,
      },
      { stack: true }
    );

    this.selectionService.syncSelection({
      rowId,
      scopeUid: this.scopeUid,
      eligiblePlacements,
      selection: {
        optionId: event.optionId,
        materialColor: event.materialColor,
        item: event.item,
        eligiblePlacements,
        placementsToRemove: event.placementsToRemove,
        placementsToRestore: event.placementsToRestore,
      },
    });
  }
}
